/**
 * AccessibilityStatementPage - הצהרת נגישות
 *
 * SOURCE OF TRUTH: "הצהרת נגישות - יפתח ונגר - יולי 2026.docx" — the version
 * drafted and approved by counsel. The text below is a verbatim transcription;
 * do NOT reword it. Any change must come from an updated signed document.
 *
 * Legally required under חוק שוויון זכויות לאנשים עם מוגבלות, התשנ"ח-1998
 * and תקנות שוויון זכויות לאנשים עם מוגבלות (התאמות נגישות לשירות), התשע"ג-2013
 * (ת"י 5568 / WCAG). Public route: /accessibility — reachable without login
 * from every page, and from the post-signing legal consent modal.
 */

import LegalDocument, { LegalContact, type Section } from '../components/legal/LegalDocument'

const PHONE = '055-9859785'
const EMAIL = 'iftach.venger@gmail.com'
const LAST_UPDATED = 'יולי 2026'

const SECTIONS: Section[] = [
  {
    title: 'הצהרת הנגישות - פתיח',
    intro: [
      'יפתח ונגר מ.ע 034025197 ("החברה" ו/או "מפעיל האתר") שואף לספק חווית שימוש מרבית באתר לכלל הציבור לרבות ציבור בעלי המוגבלויות, השקענו מאמצים רבים במטרה לאפשר, להקל ולייעל את השימוש באתר בדגש על צרכי ציבור זה.',
    ],
  },
  {
    title: 'התוסף',
    clauses: [
      { text: 'לשם הנגשת האתר, הטמענו תוסף נגישות (להלן: "התוסף").' },
      {
        text: 'באמצעות התוסף, ניתן להטמיע באתר פתרונות נגישות מתקדמים, מבלי לעשות שינויים בקוד האתר (התאמה לרגולציית ADA).',
      },
    ],
  },
  {
    title: 'רמת הנגישות',
    clauses: [
      {
        text: 'האתר פועל לפי לדרישות תקנות שיוויון זכויות לאנשים עם מוגבלות (התאמות נגישות לשירות), התשע"ג 2013.',
      },
      {
        text: 'התאמות הנגישות בוצעו עפ"י המלצות התקן הישראלי (ת"י 5568) לנגישות תכנים באינטרנט ברמת AA.',
      },
      {
        text: 'תקן ישראלי זה הינו זהה למסמך הקווים המנחים של הארגון הבינלאומי העוסק בהנגשת אתרים W3C- מסמך WCAG 2.0.',
      },
    ],
  },
  {
    title: 'תיקונים והתאמות שבוצעו',
    clauses: [
      { text: 'ההתאמות מותאמות לתצוגה במרבית הדפדפנים וכן לשימוש בטלפון סלולרי.' },
      {
        text: 'בין היתר, באמצעות התוסף, המשתמש יוכל להשתמש בתוספים כדלקמן:',
        sub: [
          'ניגודיות צבעים באתר;',
          'הגדלת הטקסט;',
          'סמן מוגדל;',
          'יישור הטקסט;',
          'גופן קריא;',
          'ועוד, כפי שמפורט בתוסף באתר.',
        ],
      },
    ],
  },
  {
    title: 'החרגות',
    intro: [
      'חרף מאמצנו לאפשר גלישה באתר נגיש עבור כל דפי האתר, יתכן ויתגלו דפים באתר שטרם הונגשו, או שטרם נמצא הפתרון הטכנולוגי המתאים. אנו ממשיכים במאמצים לשפר את נגישות האתר, ככל האפשר, וזאת מתוך אמונה ומחויבות מוסרית לאפשר שימוש באתר לכלל האוכלוסייה לרבות אנשים עם מוגבלויות.',
    ],
  },
]

export default function AccessibilityStatementPage() {
  return (
    <LegalDocument
      title="הצהרת נגישות"
      subtitle='הצהרת נגישות – אתר "יפתח ונגר – יעוץ פיננסי חכם"'
      lastUpdated={LAST_UPDATED}
      sections={SECTIONS}
      numbered={false}
    >
      <section className="mt-8">
        <h2 className="text-xl font-bold text-gray-900 mb-3">פרטי אחראי נגישות</h2>
        <ul className="list-none p-0 space-y-1">
          <li>
            <strong>שם:</strong> יפתח ונגר
          </li>
          <li>
            <strong>טלפון:</strong>{' '}
            <a className="text-primary-700 underline" href={`tel:${PHONE}`} dir="ltr">
              {PHONE}
            </a>
          </li>
          <li>
            <strong>דוא"ל:</strong>{' '}
            <a className="text-primary-700 underline" href={`mailto:${EMAIL}`} dir="ltr">
              {EMAIL}
            </a>
          </li>
        </ul>
      </section>

      <section className="mt-8">
        <h2 className="text-xl font-bold text-gray-900 mb-3">יצירת קשר</h2>
        <ul className="list-disc pr-6 space-y-1.5">
          <li>נשמח לתת מענה לכל טענה בנוגע לנושאי הנגישות.</li>
          <li>
            על מנת שנוכל להתייחס לטענה נודה על פנייה רחבה ככל שניתן הכוללת את תיאור הבעיה, את הדפדפן
            בו השתמשתם, את העמוד שבו ניסיתם לבצע פעולה שלא צלחה וכיוצ"ב.
          </li>
          <li>את הפנייה ניתן לעשות באמצעים הבאים:</li>
        </ul>
        <LegalContact phone={PHONE} email={EMAIL} />
      </section>
    </LegalDocument>
  )
}
